import arriereBackoffice, {
  EmailInvoiceFileStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

const fileFields = {
  id: true,
  assetId: true,
  source: true,
  status: true,
  problem: true,
  error: true,
  vendorId: true,
  invoiceNumber: true,
  total: true,
  periodStart: true,
  periodEnd: true,
  receivedAt: true,
  reviewedAt: true,
  createdAt: true,
  updatedAt: true,
} as const

const bookingFields = {
  id: true,
  hash: true,
  status: true,
  guestName: true,
  period: { start: true, end: true },
  home: { id: true, name: true },
} as const

const draftCaseFields = {
  id: true,
  status: true,
  taxonomyId: true,
  locationId: true,
  locationName: true,
  workOrderId: true,
  expenses: {
    id: true,
    description: true,
    amount: true,
    expenseId: true,
    bookingId: true,
    bookingStatus: true,
    booking: bookingFields,
    lineKind: true,
    flags: { kind: true, details: true },
    expectedAmount: true,
  },
} as const

const reviewFields = {
  duplicateOf: true,
  extractedText: true,
  fileUrl: true,
  file: fileFields,
  draftCases: draftCaseFields,
} as const

const relatedCasesFields = {
  relatedCaseIds: true,
  openCaseIds: true,
} as const

const vendorFields = {
  payeeVendorId: true,
  kind: true,
  prompt: true,
  invoiceEmails: true,
} as const

interface FilesParams {
  status?: EmailInvoiceFileStatus
  vendorId?: string
  limit?: number
}

export const getFiles = async (params: FilesParams) => {
  const response = await arriereBackoffice.queries.emailInvoices({
    __alias: 'emailInvoiceFiles',
    files: { __args: params, ...fileFields },
  })
  return response.files
}

export const getReview = async (id: string) => {
  const response = await arriereBackoffice.queries.emailInvoices({
    __alias: 'emailInvoiceReview',
    file: { __args: { id }, ...reviewFields },
  })
  return response.file
}

export type EmailInvoiceReview = Awaited<ReturnType<typeof getReview>>
export type DraftCase = EmailInvoiceReview['draftCases'][number]
export type DraftExpense = DraftCase['expenses'][number]
export type DraftExpenseFlag = DraftExpense['flags'][number]

export const getRelatedCases = async (draftCaseId: string) => {
  const response = await arriereBackoffice.queries.emailInvoices({
    __alias: 'emailInvoiceRelatedCases',
    emailInvoiceRelatedCases: { __args: { draftCaseId }, ...relatedCasesFields },
  })
  return response.emailInvoiceRelatedCases
}

export type RelatedCases = Awaited<ReturnType<typeof getRelatedCases>>

interface PrepareUploadParams {
  contentHash: string
  fileName: string
  size: number
}

export const prepareUpload = async (params: PrepareUploadParams) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'prepareEmailInvoiceUpload',
    prepareEmailInvoiceUpload: {
      __args: params,
      assetId: true,
      uploadUrl: true,
      existingFile: fileFields,
    },
  })
  return response.prepareEmailInvoiceUpload
}

interface SubmitParams {
  assetId: string
  vendorId?: string
}

export const submit = async (params: SubmitParams) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'submitVendorInvoice',
    submitVendorInvoice: { __args: params, ...fileFields },
  })
  return response.submitVendorInvoice
}

interface RetryParams {
  id: string
  vendorId?: string
}

export type EmailInvoiceFile = NonNullable<
  Awaited<ReturnType<typeof prepareUpload>>['existingFile']
>

export const retry = async (params: RetryParams) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'retryEmailInvoiceProcessing',
    retryEmailInvoiceProcessing: { __args: params, ...fileFields },
  })
  return response.retryEmailInvoiceProcessing
}

/** `bookingReference` links the line to a booking by its hash or OTA confirmation number. */
export interface DraftExpenseEdit {
  id?: string
  description: string
  amount: number
  bookingReference?: string
}

export interface DraftCaseEdit {
  id: string
  taxonomyId?: string
  locationId?: string
  expenses: DraftExpenseEdit[]
}

export const saveDraftCase = async (params: DraftCaseEdit) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'saveEmailInvoiceDraftCase',
    saveEmailInvoiceDraftCase: { __args: params, ...reviewFields },
  })
  return response.saveEmailInvoiceDraftCase
}

export interface DraftCaseCreate {
  fileId: string
  taxonomyId?: string
  locationId: string
  expenses: DraftExpenseEdit[]
}

export const createDraftCase = async (params: DraftCaseCreate) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'createEmailInvoiceDraftCase',
    createEmailInvoiceDraftCase: { __args: params, ...reviewFields },
  })
  return response.createEmailInvoiceDraftCase
}

export const deleteDraftCase = async (id: string) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'deleteEmailInvoiceDraftCase',
    deleteEmailInvoiceDraftCase: { __args: { id }, ...reviewFields },
  })
  return response.deleteEmailInvoiceDraftCase
}

interface PeriodParams {
  id: string
  periodStart: string
  periodEnd: string
}

export const updatePeriod = async (params: PeriodParams) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'updateEmailInvoicePeriod',
    updateEmailInvoicePeriod: { __args: params, ...reviewFields },
  })
  return response.updateEmailInvoicePeriod
}

export const confirmDrafts = async (id: string) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'confirmEmailInvoiceDrafts',
    confirmEmailInvoiceDrafts: { __args: { id }, ...fileFields },
  })
  return response.confirmEmailInvoiceDrafts
}

export const approveExpenses = async (id: string) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'approveEmailInvoiceExpenses',
    approveEmailInvoiceExpenses: { __args: { id }, ...fileFields },
  })
  return response.approveEmailInvoiceExpenses
}

export const reject = async (id: string) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'rejectEmailInvoice',
    rejectEmailInvoice: { __args: { id }, ...fileFields },
  })
  return response.rejectEmailInvoice
}

export const getVendors = async () => {
  const response = await arriereBackoffice.queries.emailInvoices({
    __alias: 'emailInvoicingPayees',
    emailInvoicingPayees: vendorFields,
  })
  return response.emailInvoicingPayees
}

export type Vendor = Awaited<ReturnType<typeof getVendors>>[number]

/** An empty prompt returns the vendor to the common prompt. */
export const setVendorPrompt = async (payeeVendorId: string, prompt?: string) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'setEmailInvoicingPayeePrompt',
    setEmailInvoicingPayeePrompt: {
      __args: { payeeVendorId, prompt: prompt || undefined },
      ...vendorFields,
    },
  })
  return response.setEmailInvoicingPayeePrompt
}

export interface VendorEmailsEdit {
  payeeVendorId: string
  emails: string[]
}

export const setVendorEmails = async (params: VendorEmailsEdit) => {
  const response = await arriereBackoffice.mutations.emailInvoices({
    __alias: 'setEmailInvoicingPayeeEmails',
    setEmailInvoicingPayeeEmails: {
      __args: params,
      ...vendorFields,
    },
  })
  return response.setEmailInvoicingPayeeEmails
}

const bookingSearchSize = 10

export const searchBookings = async (filter: string) => {
  const response = await arriereBackoffice.queries.bookings({
    __alias: 'emailInvoiceBookingSearch',
    __args: { filter, pagination: { page: 1, size: bookingSearchSize } },
    results: bookingFields,
  })
  return response.results
}

export const getVendorName = async (vendorId: string) => {
  const response = await arriereBackoffice.queries.vendors({
    __alias: 'emailInvoiceVendorName',
    findVendor: { __args: { vendorId }, name: true },
  })
  return response.findVendor?.name
}

export const getHomeName = async (homeId: string) => {
  const response = await arriereBackoffice.queries.home({
    __alias: 'emailInvoiceHomeName',
    __args: { homeId },
    name: true,
  })
  return response.name
}
